import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type PayoutKYCRecord, type PayoutKYCStats, type PayoutKYCRail } from '@/lib/adminApi';
import {
  Card,
  DataTable,
  Spinner,
  EmptyState,
  Badge,
  StatTile,
  type Column,
  type BadgeTone,
} from '@/components/ui';
import { IconShield } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { PayoutKYCControl } from '@/components/admin/PayoutKYCControl';

const PAGE = 25;

/** The two queue lanes. This page is a WORK QUEUE over the same driver bank/KYC
 *  records the Payout-KYC directory lists:
 *
 *   - 'ready'   — the driver submitted a payable target (a PayID or a BSB) AND
 *                 the record is NOT yet verified, so it is AWAITING a finance
 *                 review. This is the actionable queue, ordered oldest-first
 *                 (FIFO) so the longest-waiting driver is reviewed next.
 *   - 'blocked' — the record is INCOMPLETE (neither a PayID nor a BSB), so there
 *                 is nothing to pay out to yet. It can't be reviewed until the
 *                 driver finishes their details — surfaced separately so it never
 *                 clogs the actionable queue.
 *
 * This mirrors the legacy Filament DriverBankKycDetailResource's Pending/Verified
 * status split. Both lanes map onto the /v1/admin/payout-kyc `facet` filter,
 * paginated SERVER-SIDE (the backend returns only the matching page + an accurate
 * total, so the Pager works past the first 25 rows):
 *   ready   → facet 'awaiting' + order 'oldest' (has a target, not yet verified)
 *   blocked → facet 'incomplete'. */
type Lane = 'ready' | 'blocked';

// The payout RAIL → badge tone (kept in sync with admin-svc's payoutKYCRail and
// the directory page). A PayID/bank record is a usable payout target; a record
// with neither is incomplete — nothing to pay out to yet.
const RAIL_TONE: Record<PayoutKYCRail, BadgeTone> = {
  payid: 'ok',
  bank: 'info',
  none: 'warn',
};

function railLabel(rail: PayoutKYCRail): string {
  if (rail === 'payid') return 'PayID';
  if (rail === 'bank') return 'Bank (BSB)';
  return 'Incomplete';
}

function railBadge(k: PayoutKYCRecord) {
  return <Badge tone={RAIL_TONE[k.rail] ?? 'neutral'}>{railLabel(k.rail)}</Badge>;
}

/** A coarse "how long has this been waiting" label from a submitted/updated
 *  timestamp — the queue-age signal that turns a flat list into a triage view.
 *  Returns e.g. "just now", "3h", "2d", "5w". Defensive on a bad/empty date. */
function waitingFor(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

/** A record is "stale" (worth flagging warn) once it has been waiting on review
 *  for more than 7 days — the threshold that turns the age column amber. */
function isStale(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then > 7 * 24 * 60 * 60 * 1000;
}

/** Driver Bank-KYC Review Queue — the finance work queue over the driver bank /
 *  PayID records that gate Azupay earnings cash-outs (driver-svc OWNS the record
 *  in its driver_kyc table; admin-svc only READS through the shared pool). It is
 *  the action-first companion to the Payout-KYC directory: instead of browsing
 *  everyone, it triages the records waiting on a finance decision.
 *
 *  Two lanes (see Lane above): "Awaiting review" (a payable target submitted,
 *  oldest-first) and "Blocked — incomplete" (no target yet). Open a row to review
 *  the full bank/PayID detail and verify (the same gated, audited control the
 *  Payouts + directory pages use).
 *
 *  DEFENSIVE: shows a clean "not yet in Postgres" state when driver-svc's
 *  driver_kyc table isn't in this DB yet. READ-ONLY page — the verify WRITE is
 *  honestly surfaced as "not available yet" until the owning service grows an
 *  internal verify endpoint (driver_kyc has no verification-status column today),
 *  so the control stays discoverable + audited rather than a dead button. Mirrors
 *  the legacy Filament DriverBankKycDetailResource (its Pending/Verified split). */
export function PayoutKycReview() {
  const [lane, setLane] = useState<Lane>('ready');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<PayoutKYCRecord | null>(null);

  // Both lanes are paginated SERVER-SIDE now (the backend returns only the
  // matching page + an accurate total, so the Pager works past the first 25):
  //   ready   → facet 'awaiting' (has a payout target AND not yet verified),
  //             ordered 'oldest' (FIFO — the longest-waiting driver first).
  //   blocked → facet 'incomplete' (no payout target yet), natural recent order.
  const facet = lane === 'ready' ? 'awaiting' : 'incomplete';
  const order = lane === 'ready' ? 'oldest' : undefined;

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'payout-kyc-review', lane, q, offset],
    queryFn: ({ signal }) =>
      adminApi.payoutKYC({ q, facet, order, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;

  // The server already filtered + ordered the page for this lane, so the rows
  // render as-returned (no client-side slicing — that was the paging bug: it hid
  // every waiting driver beyond the first 25).
  const rows: PayoutKYCRecord[] = data?.items ?? [];

  // "Awaiting review" count for the lane chip + header. The backend now computes
  // it directly (has a target AND not verified); fall back to submitted-minus-
  // incomplete for an older backend that doesn't send awaiting_count.
  const awaiting = data?.stats
    ? data.stats.awaiting_count ?? data.stats.total - data.stats.incomplete_count
    : null;

  const columns: Array<Column<PayoutKYCRecord>> = [
    {
      key: 'pos',
      header: '#',
      // Queue position (1-based) in the oldest-first 'ready' lane. The page is
      // server-paginated, so the position is the row's index within this page
      // PLUS the page offset (so page 2 continues 26, 27, …).
      cell: (r) =>
        lane === 'ready' ? (
          <span className="metric text-xs text-muted">{offset + rows.indexOf(r) + 1}</span>
        ) : (
          <span className="text-xs text-muted">—</span>
        ),
    },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    {
      key: 'rail',
      header: 'Payout rail',
      cell: (r) => railBadge(r),
    },
    {
      key: 'target',
      header: 'Pay target',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.pay_id ? r.pay_id : r.bsb ? `BSB ${r.bsb}` : '—'}
        </span>
      ),
    },
    {
      key: 'bank',
      header: 'Bank',
      cell: (r) => <span className="text-xs text-slate-300">{r.bank_name || '—'}</span>,
    },
    {
      key: 'waiting',
      header: 'Waiting',
      cell: (r) => (
        <span className={isStale(r.updated_at) ? 'text-xs font-medium text-warn' : 'text-xs text-muted'}>
          {waitingFor(r.updated_at)}
        </span>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          {lane === 'ready' ? 'Review' : 'View'}
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver Bank-KYC Review Queue"
        subtitle="Driver bank / PayID records waiting on a finance review before Azupay payouts. Oldest submissions first — open one to review the detail and verify."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by driver / bank / PayID…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver bank-KYC not yet in Postgres"
            description={
              data?.note ??
              'The driver bank/KYC records are owned by driver-svc and are not in this Postgres yet. This queue lights up automatically once the table lands.'
            }
            icon={<IconShield className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <ReviewStatsRow stats={data.stats} awaiting={awaiting} /> : null}

          {/* Lane switch — the two queue states (mirrors the legacy Pending/Verified
              filter, reframed as Awaiting / Blocked since there is no verified flag). */}
          <div className="mb-3 flex flex-wrap gap-1.5">
            <LaneChip
              active={lane === 'ready'}
              onClick={() => {
                setLane('ready');
                setOffset(0);
              }}
              label="Awaiting review"
              count={awaiting}
            />
            <LaneChip
              active={lane === 'blocked'}
              onClick={() => {
                setLane('blocked');
                setOffset(0);
              }}
              label="Blocked — incomplete"
              count={data?.stats?.incomplete_count ?? null}
            />
          </div>

          {lane === 'blocked' ? (
            <p className="mb-3 text-xs text-muted">
              These drivers started their bank/KYC details but have not submitted a payable target (a PayID or a
              BSB) yet, so there is nothing to verify. They appear here until they complete their details.
            </p>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading review queue…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load the review queue" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<PayoutKYCRecord>
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => String(r.driver_id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title={lane === 'ready' ? 'Queue clear' : 'No incomplete records'}
                      description={
                        lane === 'ready'
                          ? 'No driver bank/PayID records are waiting on a review right now.'
                          : 'Every submitted record has a payable target.'
                      }
                      icon={<IconShield className="h-6 w-6" />}
                    />
                  }
                />
                <Pager
                  total={data?.total ?? 0}
                  offset={offset}
                  pageSize={PAGE}
                  onOffset={setOffset}
                  fetching={isFetching}
                />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <ReviewDrawer record={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** One queue-lane chip — a labelled toggle with a live count pill. */
function LaneChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'inline-flex items-center gap-1.5 rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40'
          : 'inline-flex items-center gap-1.5 rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800'
      }
    >
      {label}
      {count !== null ? (
        <span className="metric rounded bg-ink-900/60 px-1.5 text-[11px] text-slate-400">{count}</span>
      ) : null}
    </button>
  );
}

/** The headline totals above the queue: awaiting / by rail / blocked. */
function ReviewStatsRow({ stats, awaiting }: { stats: PayoutKYCStats; awaiting: number | null }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile
        label="Awaiting review"
        value={awaiting !== null ? String(awaiting) : '—'}
        tone={awaiting && awaiting > 0 ? 'info' : 'neutral'}
        hint="have a payout target"
      />
      <StatTile label="PayID" value={String(stats.payid_count)} hint="Azupay PayID set" />
      <StatTile label="Bank (BSB)" value={String(stats.bank_count)} hint="bank transfer set" />
      <StatTile
        label="Blocked"
        value={String(stats.incomplete_count)}
        tone={stats.incomplete_count > 0 ? 'warn' : 'neutral'}
        hint="no payout target yet"
      />
    </div>
  );
}

/** ReviewDrawer — the full bank/PayID detail + the gated payout-KYC verify
 *  control. The queue row already carries every field, so no extra fetch is
 *  needed for the body. */
function ReviewDrawer({ record: k, onClose }: { record: PayoutKYCRecord; onClose: () => void }) {
  const payIdTypeLabel = k.pay_id_type ? k.pay_id_type.toUpperCase() : '';
  return (
    <Drawer
      title={`Bank-KYC review · ${k.driver_name}`}
      subtitle={`Driver #${k.driver_id} · ${railLabel(k.rail)} · waiting ${waitingFor(k.updated_at)}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Payout rail" value={railBadge(k)} />
          <Field label="Driver" value={`${k.driver_name} (#${k.driver_id})`} />
          {k.phone ? <Field label="Phone" value={<span className="metric">{k.phone}</span>} /> : null}
          {k.email ? <Field label="Email" value={k.email} /> : null}
        </div>

        {/* Bank-transfer fields (BSB rail). */}
        <Card>
          <div className="mb-2 text-sm font-medium text-slate-100">Bank details</div>
          <Field label="Bank name" value={k.bank_name || '—'} />
          <Field label="Account name" value={k.account_name || '—'} />
          <Field
            label="Account number"
            value={<span className="metric">{k.bank_account_number || '—'}</span>}
          />
          <Field label="BSB" value={<span className="metric">{k.bsb || '—'}</span>} />
        </Card>

        {/* PayID fields (Azupay's preferred rail). */}
        <Card>
          <div className="mb-2 text-sm font-medium text-slate-100">PayID</div>
          <Field label="PayID" value={<span className="metric">{k.pay_id || '—'}</span>} />
          <Field label="PayID type" value={payIdTypeLabel || '—'} />
          <Field label="Submitted" value={dateTime(k.created_at)} />
          <Field label="Last updated" value={dateTime(k.updated_at)} />
        </Card>

        {k.rail === 'none' ? (
          <Card>
            <div className="mb-1 text-sm font-medium text-slate-100">Incomplete — nothing to verify</div>
            <p className="text-xs text-muted">
              This driver has not submitted a payable target (a PayID or a BSB) yet, so there is nothing to
              verify. They will move into the review queue automatically once they complete their details.
            </p>
          </Card>
        ) : (
          /* Payout-KYC verify for this driver (gated by payouts.kyc_verify). A
             REAL audited write — admin-svc owns the verification columns
             (migration 037). Degrades to a clean "not available yet" note until
             the table + migration land. */
          <PayoutKYCControl driverId={k.driver_id} record={k} />
        )}
      </div>
    </Drawer>
  );
}
